import { LoanOfferProductSubType, LoanRatesFilter, RESULTS_URL_KEY } from 'embed/constants'
import { CommonState, MultiProductState } from 'embed/reducers'
import { FiltersState } from 'embed/reducers/filters'
import { RatesState, SortedState as SortedRateState } from 'embed/reducers/rates'
import { RatesFiltersState } from 'embed/reducers/ratesFilter'
import { DepositsOffer, LifeInsuranceOffer, LoanOffer, Offer } from 'embed/constants/models'
import {
  Config,
  ConfigKeys,
  FeatureFlag,
  Filter,
  LenderIdKeys,
  OfferStyleOptions,
  Product,
  QueryParamKeys,
} from '@evenfinancial/even-ts-static'

import { createSelector } from 'reselect'
import {
  BESTOW_COMPANY_UUID_DEV,
  BESTOW_COMPANY_UUID_PROD,
  HAVEN_SIMPLE_UUID_DEV,
  HAVEN_SIMPLE_UUID_PROD,
  MOO_COMPANY_UUID_DEV,
  MOO_COMPANY_UUID_PROD,
} from 'ui/multi-product/constants'
import {
  clientTagsSelector,
  configFeatureFlagsSelector,
  configResultsUuidSelector,
  configShouldAutoRedirectSelector,
  configStateSelector,
} from './config'
import {
  loanAmountStateSelector,
  loanOffersSelector,
  ratesFilteredLifeInsuranceOffersSelector,
  ratesFiltersSelector,
  ratesMaxResultsSelector,
  ratesSortedOffersSelector,
} from './rates'
import { uiSelectedStepSelector } from './ui'
import { groupOffersByLenderId } from '../utils/sort-offers'
import { filtersProductSelector } from './filters'
import { filterUniqueLoanOffersByCompanyUuid } from 'embed/services/OfferFilters'
import { DeepReadonly } from 'utility-types'
import { defaultCreditScoreUrl } from '../constants/config'

export interface SelectorProps {
  offersPath?: string
  product?: Product
}

export interface TotalSelectorProps {
  product: Product
}

interface SelectorStateReadonly {
  config: Config
  filters: FiltersState
  rates: RatesState
}

export type SelectorState = DeepReadonly<SelectorStateReadonly>

export const getMaxResults = (state: SelectorState, props: SelectorProps): number =>
  (props.product && ratesMaxResultsSelector(state as CommonState, props.product)) ?? 10
const getTotalSelectorProps = (_, props: SelectorProps) => ({ product: props.product })
const getSortedRates = (state: SelectorState, props: SelectorProps) => {
  const path = props.offersPath ?? ''
  const [product, offersPath] = path.split('.')
  return ratesSortedOffersSelector(state as CommonState)[product]?.[offersPath]
}
const getConfig = (state: SelectorState) => state.config
const getLenderFilter = (state: SelectorState, props: SelectorProps) =>
  props.product ? filtersProductSelector(state as CommonState, props.product)?.[Filter.LenderId] : undefined
export const getPreferredOfferPartnerSelector = (state: SelectorState) => getConfig(state).preferred_offer_partner
const getProductSubtypeFilter = (state: SelectorState, props: SelectorProps) =>
  props.product ? filtersProductSelector(state as CommonState, props.product)?.[Filter.ProductSubType] : undefined
const getRatesFilters = (state: SelectorState) => ratesFiltersSelector(state as CommonState)

export const ratesFilteredLoanOffersSelector = (
  rates: LoanOffer[],
  ratesFilters: DeepReadonly<{ [key in string | number | symbol]: string[] }>,
) => {
  let filteredRates = rates
  Object.keys(ratesFilters).forEach((filterKey) => {
    const filterValues = ratesFilters[filterKey]
    if (filterValues.length) {
      switch (filterKey) {
        case LoanRatesFilter.AprType:
          filteredRates = filteredRates?.filter((offer) => filterValues.includes(offer.aprType ?? ''))
          break
        case LoanRatesFilter.ProductSubType:
          filteredRates = filteredRates?.filter((offer) => filterValues.includes(offer.productSubType))
          break
        default:
      }
    }
  })
  return filteredRates
}

const getOffetLenderId = (offer: Offer, product: Product) => {
  switch (product) {
    case Product.Loan:
      return (offer as LoanOffer).originator?.companyUuid
    case Product.Deposits:
      return (offer as DepositsOffer).partner?.uuid
    default:
      return ''
  }
}

export const filteredRatesSelector = createSelector(
  [
    getMaxResults,
    getTotalSelectorProps,
    getSortedRates,
    getConfig,
    getLenderFilter,
    getProductSubtypeFilter,
    getRatesFilters,
  ],
  (
    maxResults: number,
    props: TotalSelectorProps,
    rates: Offer[],
    config: Config,
    lenderFilter?: string,
    productSubTypeFilter?: string,
    ratesFilters?: RatesFiltersState,
  ) => {
    if (!rates) return []

    let filteredRates

    switch (props.product) {
      case Product.Deposits:
        // Filter out results that don't match the product sub-type.
        // For now, only do this for deposits to be safe.
        if (productSubTypeFilter) {
          filteredRates = rates.filter((offer: DepositsOffer) => offer.productSubType === productSubTypeFilter)
        } else {
          filteredRates = rates
        }
        break
      case Product.Loan:
        // TODO: generalize, refactor lenderFilter, maxResults, deposit productSubTypeFilter to be ratesFilters
        if (
          ratesFilters?.[Product.Loan] &&
          Object.values(ratesFilters[Product.Loan])
            .map((filterArr) => filterArr.length)
            .reduce((f, g) => f + g)
        ) {
          filteredRates = ratesFilteredLoanOffersSelector(rates as LoanOffer[], ratesFilters[Product.Loan])
        } else {
          filteredRates = rates
        }
        break
      case Product.LifeInsurance:
        filteredRates = ratesFilteredLifeInsuranceOffersSelector(
          rates as LifeInsuranceOffer[],
          ratesFilters?.[Product.LifeInsurance],
        )
        break
      default:
        filteredRates = rates
    }

    if (lenderFilter !== undefined) {
      // Filter to results from specific lender
      filteredRates = filteredRates.filter((offer) => {
        const offerLenderId = getOffetLenderId(offer, props.product)
        return lenderFilter === offerLenderId
      })
    } else {
      // Filter to one offer per lender
      switch (props.product) {
        case Product.Loan:
          filteredRates = filterUniqueLoanOffersByCompanyUuid(filteredRates)
          break
        default:
      }
    }

    return filteredRates.slice(0, maxResults)
  },
)

// Get a map from lender to a list of offers. Helpful in to determine if we have "more offers to show" for a lender
export const unfilteredRatesByLenderSelector = createSelector(
  [getTotalSelectorProps, getSortedRates],
  (props: TotalSelectorProps, rates: SortedRateState) =>
    groupOffersByLenderId(rates as Offer[], LenderIdKeys[props.product]),
)

// Assumed sort direction for array.prototype.sort is ASC however we want to sort by DESC yet keep secured loans at the bottom
// Hence why the return values of -1 and 1 may seem reversed
export const sortPersonalLoansFirst = (a: LoanOffer, b: LoanOffer): number =>
  a.productSubType !== LoanOfferProductSubType.PersonalLoan && b.productSubType === LoanOfferProductSubType.PersonalLoan
    ? 1
    : a.productSubType === LoanOfferProductSubType.PersonalLoan &&
      b.productSubType !== LoanOfferProductSubType.PersonalLoan
    ? -1
    : 0

// Include all product subtypes but for Personal Loans and Student Loan Refinance,
// only include preApproved or preQualified offers.
export const loanOfferFilterFunction = (offer: LoanOffer): boolean =>
  offer.preApproved ||
  offer.preQualified ||
  (offer.productSubType !== undefined &&
    ![LoanOfferProductSubType.PersonalLoan, LoanOfferProductSubType.StudentLoanRefinance].includes(
      offer.productSubType,
    ) &&
    Object.values(LoanOfferProductSubType).includes(offer.productSubType))

export const recommendedLoanOffersSelector = createSelector([loanOffersSelector], (loanOffers) =>
  loanOffers
    .filter(loanOfferFilterFunction)
    .sort((a, b) => (a.maxApr ?? 0) - (b.maxApr ?? 0))
    .sort((a, b) => (b.recommendationScore ?? 0) - (a.recommendationScore ?? 0))
    .sort(sortPersonalLoansFirst),
)

export const conversionProbabilityLoanOffersSelector = createSelector([loanOffersSelector], (loanOffers) =>
  loanOffers
    .filter(loanOfferFilterFunction)
    .sort((a, b) => (a.maxApr ?? 0) - (b.maxApr ?? 0))
    .sort((a, b) => (b.conversionProbability ?? 0) - (a.conversionProbability ?? 0))
    .sort(sortPersonalLoansFirst),
)

export const lowestAprLoanOffersSelector = createSelector([loanOffersSelector], (loanOffers) =>
  loanOffers
    .filter(loanOfferFilterFunction)
    .sort((a, b) => (a.maxApr ?? 0) - (b.maxApr ?? 0))
    .sort(sortPersonalLoansFirst),
)

export const closestLoanAmountOffersSelector = createSelector(
  [loanOffersSelector, loanAmountStateSelector],
  (loanOffers, loanAmount) =>
    loanOffers
      .filter(loanOfferFilterFunction)
      .sort((a, b) => (b.recommendationScore ?? 0) - (a.recommendationScore ?? 0))
      .sort((a, b) => Math.abs(loanAmount - (a?.maxAmount ?? 0)) - Math.abs(loanAmount - (b?.maxAmount ?? 0)))
      .sort(sortPersonalLoansFirst),
)

export const offerStyleIsCompactSelector = (state: CommonState) =>
  state.config[QueryParamKeys.OfferStyle] === OfferStyleOptions.Compact

const creditScoreOfferUrlSelector = (state: CommonState): string | undefined =>
  configStateSelector(state)[ConfigKeys.CreditScoreOfferUrl]

export const creditScoreOfferUrlOrDefault = (state: CommonState): string => {
  const configOfferUrl = creditScoreOfferUrlSelector(state)
  return configOfferUrl ?? defaultCreditScoreUrl
}

const partnerKeysDotDash = new Set([
  'balance-custom-embed-loans',
  'the-balance-embed-loans',
  'investopedia-custom-embed-loans',
  'investopedia-embed-loans',
])
const companyUuidsDotDash = new Set([
  'f4dbaaf7-76c8-4e2b-b8e2-94a09d7638b1',
  '2a702bfa-c31b-4330-8ded-b7c8ee5e031e',
  '25fc330c-a740-4978-9f2e-eb1705000029',
  'beee3060-fdf7-40d0-a491-b67f31f43e10',
])
export const recommendedTextNotShown = (state: CommonState) => {
  const { config } = state
  return (
    (config.partner !== undefined && partnerKeysDotDash.has(config.partner)) ||
    (config.company_uuid !== undefined && companyUuidsDotDash.has(config.company_uuid))
  )
}

export const shouldDisplayOnLendersSiteLabel = (state: CommonState) => {
  const { config } = state
  return (
    (config.partner !== undefined && partnerKeysDotDash.has(config.partner)) ||
    (config.company_uuid !== undefined && companyUuidsDotDash.has(config.company_uuid))
  )
}

/**
 * Is shouldAutoRedirect a QP (default is true if QP does not exist)
 * and did the user not arrive from an email
 */
export const setShouldAutoRedirectSelector = createSelector(
  [configShouldAutoRedirectSelector, clientTagsSelector],
  (shouldAutoRedirect, clientTags) => shouldAutoRedirect && !clientTags?.src?.includes('email'),
)

export const validationDataSelector = (state: CommonState) => ({
  LoanPurpose: state?.filters?.[Product.Loan]?.[Filter.LoanPurpose],
})

export const shouldHaveNoMedicalExamOfferSelector = (uuid): boolean =>
  [
    BESTOW_COMPANY_UUID_DEV,
    BESTOW_COMPANY_UUID_PROD,
    HAVEN_SIMPLE_UUID_DEV,
    HAVEN_SIMPLE_UUID_PROD,
    MOO_COMPANY_UUID_DEV,
    MOO_COMPANY_UUID_PROD,
  ].includes(uuid ?? '')

/**
 * User landed on loan results step
 * or reached loan results step from UI navigation (i.e. from application step)
 */
export const isLoanResultsViewSelector = createSelector(
  [configResultsUuidSelector, uiSelectedStepSelector],
  (resultsUuid, selectedStep) => !!resultsUuid || selectedStep[Product.Loan] === RESULTS_URL_KEY,
)

export const shouldUseRangedOfferListSelector = (state: MultiProductState) =>
  configFeatureFlagsSelector(state).includes(FeatureFlag.RangedOfferList)
